import IconButton from 'components/@extended/IconButton';
import PermissionButton from 'components/buttons/PermissionButton';
import { CreateDialogFieldCompanion } from 'components/create-dialog/CreateDialogRequired';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import FormRow from 'components/forms/FormRow';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import AddressInput from 'components/forms/inputs/rhf-inputs/AddressInput';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import NumberInput from 'components/forms/inputs/rhf-inputs/NumberInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { useIsNewDesignInput } from 'components/forms/inputs/rhf-inputs/newDesignInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { Search as SearchIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import ContactDynamicMultiSelectPicker from 'components/party/ContactDynamicMultiSelectPicker';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import {
    CUSTOMIZE_INVOICE_PERMISSIONS,
    removeEmptyStringsFromEmailList,
    validateEmails,
} from 'pages/matters/form-sections/InvoiceSpecFields';
import { customizationAfterTemplatePick } from 'pages/invoices/layout/views/general-tab/supportedCustomization';
import { useCallback } from 'react';
import { CustomizationDto } from 'types/invoices';
import { FormattedMessage } from 'react-intl';
import {
    useLazyGetMinContactsListWithFiltersQuery,
    useLazyGetMinContactsWithRelationsListWithFiltersQuery,
} from 'store/api/contacts';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { PartyType, RelationshipTypeCode } from 'types/contacts';
import { CustomField } from 'types/customFields';
import { DocumentTemplateMinDto } from 'types/document';
import { FeatureFlag } from 'types/features';
import { cn } from 'utils/cn';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { renderPartyEmail } from 'utils/partyNameUtil';
import { BillingSectionId, SPEC_INCLUDED_OPTIONS } from '../contactFormMapping';
import { ContactFormState, EINVOICE_OPERATOR_TYPES } from './useContactForm';

// The form holds a template's id alone, which a contact loads with: its name comes from the templates list.
const templateName = (templates: DocumentTemplateMinDto[] | undefined) => (option: { id: number }) =>
    templates?.find((template) => template.id === option.id)?.name ?? '';

/**
 * Billing: VAT, payment terms, billing address, e-invoicing, payer and the invoices' recipients. With a `part`, only
 * that billing section's fields, as `CONTACT_SECTION_FIELDS` lists them; without one, all of them in the form's order.
 */
export const BillingFields = ({ form, part }: { form: ContactFormState; part?: BillingSectionId }): JSX.Element => {
    const { control, setValue, getValues, trigger, formState } = form.methods;
    const {
        bankAccountsGrouped,
        classifiers,
        contact,
        groupCompanyRelated,
        invoiceSpecTemplates,
        invoiceTemplates,
        isBankAccountSelectionEnabled,
        isEinvoicingEnabled,
        isPeppol,
        isVatMandatory,
        languageOptions,
        notVatLiable,
        openAddNewContactModal,
        openContactTypePicker,
        openCustomizeInvoiceModal,
        openEinvoiceAddressPicker,
        partyType,
        relatedCompanyIds,
        relationshipType,
        validateEinvoiceAddress,
    } = form;
    // The new design asks the positive question over the same stored flag, and shows an example of the number.
    const isNewDesign = useIsNewDesignInput();
    const show = (section: BillingSectionId) => !part || part === section;
    const vat = show('billing-vat');
    const address = show('billing-address');
    const delivery = show('billing-delivery');
    const recipients = show('billing-recipients');
    const rules = show('billing-rules');
    // On the record page each field is a cell of its card's grid, where the read view has its row.
    const inSection = useInRecordSection();
    const savedTemplates = formState.defaultValues;
    const resetInvoiceCustomization = useCallback(() => {
        const customization = customizationAfterTemplatePick(
            {
                invoiceTemplateId: getValues('invoiceTemplate')?.id,
                invoiceSpecTemplateId: getValues('invoiceSpecTemplate')?.id,
            },
            {
                invoiceTemplateId: savedTemplates?.invoiceTemplate?.id,
                invoiceSpecTemplateId: savedTemplates?.invoiceSpecTemplate?.id,
                invoiceCustomization: savedTemplates?.invoiceCustomization as CustomizationDto | null | undefined,
            }
        );
        setValue('invoiceCustomization', customization, { shouldDirty: true });
    }, [getValues, savedTemplates, setValue]);
    const einvoiceSearch = !isPeppol && (
        <IconButton
            aria-label={t('einvoiceAddress.picker.open')}
            onClick={openEinvoiceAddressPicker}
            color="primary"
            // Level with the field: past the label row, which is 20px with its gap in a record section.
            className={cn('shrink-0', inSection ? 'mt-5' : 'mt-7')}
        >
            <SearchIcon />
        </IconButton>
    );

    return (
        <>
            {vat ? <CheckboxInput control={control} name="isInternalClient" label="contacts.internal-client" /> : null}
            {vat ? (
                <TextInput
                    name="clientReference"
                    control={control}
                    label="contacts.about.client-reference"
                    placeholder="contacts.about.client-reference"
                />
            ) : null}
            {vat && partyType !== PartyType.PERSON ? (
                // It waives the VAT number, so the create dialog draws it beside that field wherever the field
                // is. TextInput registers its field as `field_` + name.
                <CreateDialogFieldCompanion field="field_vatRegNum">
                    <CheckboxInput
                        control={control}
                        name="notVatLiable"
                        label={isNewDesign ? 'is-vat-liable' : 'not-vat-liable'}
                        inverted={isNewDesign}
                    />
                </CreateDialogFieldCompanion>
            ) : null}
            {vat && partyType !== PartyType.PERSON && !notVatLiable ? (
                <TextInput
                    name="vatRegNum"
                    control={control}
                    label="vat-number"
                    // A sample value, not copy: kept out of the catalog, so a session replay masks it.
                    placeholder={isNewDesign ? 'EE123456789' : 'vat-number'}
                    shouldTranslatePlaceholder={!isNewDesign}
                    required={isVatMandatory}
                />
            ) : null}
            {vat && partyType !== PartyType.PERSON && !notVatLiable ? (
                // It makes the VAT number required, so the create dialog draws it beside that field too.
                <CreateDialogFieldCompanion field="field_vatRegNum">
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="vat-country"
                        name="vatCountryCode"
                        options={getValidOptionsFromClassifier<string, string>(classifiers['COUNTRY']) ?? []}
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        placeholder="vat-country"
                        sortDirection="asc"
                    />
                </CreateDialogFieldCompanion>
            ) : null}

            {vat && relationshipType === RelationshipTypeCode.CLIENT && isBankAccountSelectionEnabled ? (
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="contacts.preferred-bank-account"
                    name="preferredBankAccount"
                    options={bankAccountsGrouped.flatMap((company) =>
                        company.bankAccounts.map((account) => ({
                            value: account.id,
                            label: account.iban,
                            group: company.companyName,
                        }))
                    )}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    placeholder="contacts.select-bank-account"
                />
            ) : null}

            {vat && relationshipType === RelationshipTypeCode.CLIENT ? (
                <NumberInput
                    control={control}
                    label="financial-conditions.payment-deadline"
                    placeholder="financial-conditions.payment-deadline"
                    name="invoicingPaymentDays"
                    inputProps={{ min: 0, max: 120 }}
                />
            ) : null}

            {rules ? (
                <TextAreaInput
                    name="billingNotes"
                    control={control}
                    label="contacts.billing-notes"
                    enableAutofill
                    tooltip={<InfoTooltip text="contacts.billing-notes-tooltip" />}
                />
            ) : null}

            {rules ? (
                <FeatureFlagged action="show" flag={FeatureFlag.INVOICING_INFO_ENABLED}>
                    <TextAreaInput
                        name="invoicingInfo"
                        control={control}
                        label="contacts.invoicing-info-internal"
                        enableAutofill
                        tooltip={<InfoTooltip text="contacts.invoicing-info-tooltip" />}
                    />
                </FeatureFlagged>
            ) : null}

            {address ? (
                <AddressInput
                    // The section's card is titled with this label, which is left to assistive technology there.
                    wrapperClassName={inSection ? '[&_[data-slot=field-label]]:sr-only' : undefined}
                    label="billing-address"
                    name="billingAddress"
                    control={control}
                    classifiers={classifiers}
                    customFields={contact?.billingAddress?.customFields as CustomField[]}
                    isBillingAddress
                    required={relationshipType === RelationshipTypeCode.CLIENT}
                    getSourceAddressForCopy={() =>
                        partyType === PartyType.COMPANY ? getValues('registrationAddress') : getValues('homeAddress')
                    }
                    partyType={partyType}
                />
            ) : null}

            {address ? (
                <FeatureFlagged
                    action="show"
                    mode="any"
                    flag={[FeatureFlag.LOCALIZED_FIELDS, FeatureFlag.LOCALIZED_FIELDS_CONTACTS]}
                >
                    <AddressInput
                        label="billing-address-local"
                        name="billingLocalAddress"
                        control={control}
                        classifiers={classifiers}
                        customFields={contact?.billingLocalAddress?.customFields as CustomField[]}
                        isBillingAddress
                        getSourceAddressForCopy={() =>
                            partyType === PartyType.COMPANY
                                ? getValues('registrationAddress')
                                : getValues('homeAddress')
                        }
                        partyType={partyType}
                    />
                </FeatureFlagged>
            ) : null}

            {delivery ? (
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="contacts.billing.invoice-lang"
                    name="preferredLanguage"
                    options={languageOptions}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    placeholder="contacts.billing.invoice-lang"
                    required={relationshipType === RelationshipTypeCode.CLIENT}
                />
            ) : null}
            {delivery && partyType === PartyType.COMPANY && isEinvoicingEnabled && (
                <>
                    <FormRow>
                        <SelectInput
                            setValue={setValue}
                            control={control}
                            label="einvoiceOperatorType.title"
                            placeholder="einvoiceOperatorType.title"
                            name="einvoiceOperatorType"
                            options={EINVOICE_OPERATOR_TYPES}
                            getOptionLabel="label"
                            getOptionIdentifier="value"
                            clearable={false}
                        />
                    </FormRow>
                    <div className={inSection ? 'contents' : 'flex w-full flex-col items-start gap-4 sm:flex-row'}>
                        <div className={inSection ? 'contents' : 'w-full sm:min-w-0 sm:flex-1'}>
                            {isPeppol ? (
                                <TextInput
                                    name="einvoiceAddress"
                                    control={control}
                                    label="einvoiceAddress.title"
                                    placeholder={t('einvoiceAddress.placeholder.peppol')}
                                />
                            ) : (
                                <TextInput
                                    name="einvoiceAddress"
                                    control={control}
                                    label="einvoiceAddress.title"
                                    placeholder={t('einvoiceAddress.placeholder.finvoice')}
                                    shouldTranslatePlaceholder={false}
                                    validate={validateEinvoiceAddress}
                                    onBlur={() => trigger('einvoiceAddress')}
                                />
                            )}
                        </div>
                        {!isPeppol && (
                            // There the operator code and the registry search share the code's cell.
                            <div
                                className={
                                    inSection ? 'form-field flex items-start gap-2' : 'w-full sm:min-w-0 sm:flex-1'
                                }
                            >
                                <TextInput
                                    name="einvoiceOperatorCode"
                                    control={control}
                                    label="einvoiceOperatorCode.title"
                                    placeholder={t('einvoiceOperatorCode.placeholder')}
                                />
                                {inSection ? einvoiceSearch : null}
                            </div>
                        )}
                        {inSection ? null : einvoiceSearch}
                    </div>
                </>
            )}
            {delivery ? (
                // One cell on the record page: the Customize button has the line under the select.
                <div className={inSection ? 'form-field flex flex-col items-start gap-1' : 'contents'}>
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="matters.invoice-template"
                        placeholder="matters.select-invoice-template"
                        name="invoiceTemplate"
                        options={invoiceTemplates?.filter((template) => !template.isCorrection) ?? []}
                        getOptionLabel={templateName(invoiceTemplates)}
                        getOptionIdentifier="id"
                        onChange={resetInvoiceCustomization}
                    />
                    <FeatureFlagged action="show" flag={FeatureFlag.USE_CUSTOMIZED_INVOICE_TEMPLATE}>
                        <div className={inSection ? 'empty:hidden' : 'flex justify-start'}>
                            <PermissionButton
                                size="extraSmall"
                                variant="outlined"
                                onClick={openCustomizeInvoiceModal}
                                // The modal opens with the templates' supported options, and its save replaces the
                                // stored customization: opened before they load, it would drop every option.
                                disabled={!invoiceTemplates || !invoiceSpecTemplates}
                                permissions={CUSTOMIZE_INVOICE_PERMISSIONS}
                            >
                                <FormattedMessage id="invoices.custom.customize" />
                            </PermissionButton>
                        </div>
                    </FeatureFlagged>
                </div>
            ) : null}
            {delivery ? (
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="matters.invoice-specification-template"
                    placeholder="matters.select-invoice-specification-template"
                    name="invoiceSpecTemplate"
                    options={invoiceSpecTemplates ?? []}
                    getOptionLabel={templateName(invoiceSpecTemplates)}
                    getOptionIdentifier="id"
                    onChange={resetInvoiceCustomization}
                />
            ) : null}
            {delivery ? (
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="invoices.general-tab.details.merge-invoice-and-spec"
                    name="isSpecIncludedInInvoice"
                    options={SPEC_INCLUDED_OPTIONS}
                    getOptionLabel="label"
                    getOptionIdentifier={(option) => String(option.value)}
                />
            ) : null}
            {vat ? (
                <ContactDynamicSelectPicker
                    setValue={setValue}
                    control={control}
                    label="contacts.billing.payer-party"
                    name="payerParty"
                    paginated={{
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                    }}
                    PickerModalComponent={ContactPickerModal}
                    placeholder="contacts.billing.select-payer-party"
                    tooltip={<InfoTooltip text="contacts.billing.payer-party-tooltip" />}
                    noOptionsContent={
                        <PermissionButton
                            permissions={[
                                'CREATE.PARTIES',
                                'CREATE.PARTIES.CLIENTS',
                                'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                            ]}
                            variant="contained"
                            color="secondary"
                            size="extraSmall"
                            onClick={() => openContactTypePicker('payerParty')}
                        >
                            <FormattedMessage id="contacts.add-new-contact" />
                        </PermissionButton>
                    }
                />
            ) : null}
            {rules ? (
                <CheckboxInput
                    control={control}
                    name="useJointInvoicing"
                    label="contacts.billing.use-joint-invoicing"
                />
            ) : null}
            {rules ? (
                <FeatureFlagged action="show" flag={FeatureFlag.INVOICING_DATE_ON_PARTY_FORM}>
                    <CheckboxInput
                        control={control}
                        name="issueInvoicesWithCurrentDate"
                        label="contacts.billing.issue-invoices-with-current-date"
                        tooltip={<InfoTooltip text="contacts.tooltips.issue-invoices-with-current-date" />}
                    />
                </FeatureFlagged>
            ) : null}
            {recipients ? (
                <ContactDynamicSelectPicker
                    setValue={setValue}
                    control={control}
                    label="matters.primary-recipient"
                    tooltip={<InfoTooltip text="matters.tooltips.primary-recipient" />}
                    placeholder="matters.select-primary-recipient"
                    name="primaryRecipientParty"
                    paginated={{
                        fetch: relatedCompanyIds?.length
                            ? useLazyGetMinContactsWithRelationsListWithFiltersQuery
                            : useLazyGetMinContactsListWithFiltersQuery,
                        filters: `partyType==${PartyType.PERSON}`,
                        searchField: ['name', 'partyNumber', 'mainEmail'],
                        additionalParams: relatedCompanyIds?.length
                            ? { prioritizeEmployeesOf: relatedCompanyIds }
                            : undefined,
                    }}
                    isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                    PickerModalComponent={ContactPickerModal}
                    {...(relatedCompanyIds?.length && {
                        groupByStaticFields: groupCompanyRelated,
                    })}
                    getOptionEndAdornment={renderPartyEmail}
                    noOptionsContent={
                        <PermissionButton
                            permissions={[
                                'CREATE.PARTIES',
                                'CREATE.PARTIES.CLIENTS',
                                'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                            ]}
                            variant="contained"
                            color="secondary"
                            size="extraSmall"
                            onClick={() => openAddNewContactModal('primaryRecipientParty', true)}
                        >
                            <FormattedMessage id="contacts.add-new-contact" />
                        </PermissionButton>
                    }
                />
            ) : null}
            {recipients ? (
                <TextAreaInput
                    control={control}
                    label="matters.primary-recipient-emails"
                    placeholder="matters.primary-recipient-emails"
                    name="primaryRecipientAdditionalEmails"
                    tooltip={<InfoTooltip text="matters.tooltips.primary-recipient-emails" />}
                    validate={validateEmails}
                    onBlur={removeEmptyStringsFromEmailList}
                />
            ) : null}
            {recipients ? (
                <ContactDynamicMultiSelectPicker
                    setValue={setValue}
                    control={control}
                    label="matters.secondary-recipient"
                    tooltip={<InfoTooltip text="matters.tooltips.secondary-recipient" />}
                    placeholder="matters.select-secondary-recipient"
                    name="secondaryRecipientParties"
                    paginated={{
                        fetch: relatedCompanyIds?.length
                            ? useLazyGetMinContactsWithRelationsListWithFiltersQuery
                            : useLazyGetMinContactsListWithFiltersQuery,
                        filters: `partyType==${PartyType.PERSON}`,
                        searchField: ['name', 'partyNumber', 'mainEmail'],
                        additionalParams: relatedCompanyIds?.length
                            ? { prioritizeEmployeesOf: relatedCompanyIds }
                            : undefined,
                    }}
                    isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                    PickerModalComponent={ContactPickerModal}
                    {...(relatedCompanyIds?.length && {
                        groupByStaticFields: groupCompanyRelated,
                    })}
                    getOptionEndAdornment={renderPartyEmail}
                    noOptionsContent={
                        <PermissionButton
                            permissions={[
                                'CREATE.PARTIES',
                                'CREATE.PARTIES.CLIENTS',
                                'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                            ]}
                            variant="contained"
                            color="secondary"
                            size="extraSmall"
                            onClick={() => openAddNewContactModal('secondaryRecipientParties', true)}
                        >
                            <FormattedMessage id="contacts.add-new-contact" />
                        </PermissionButton>
                    }
                />
            ) : null}
            {recipients ? (
                <TextAreaInput
                    control={control}
                    label="matters.secondary-recipient-emails"
                    tooltip={<InfoTooltip text="matters.tooltips.secondary-recipient-emails" />}
                    placeholder="matters.secondary-recipient-emails"
                    name="secondaryRecipientAdditionalEmails"
                    validate={validateEmails}
                    onBlur={removeEmptyStringsFromEmailList}
                />
            ) : null}
            {relationshipType == RelationshipTypeCode.CLIENT ? (
                <>
                    {rules ? (
                        <SelectInput
                            control={control}
                            label="matters.client-invoicing-deadline-day"
                            placeholder="matters.client-invoicing-deadline-day"
                            tooltip={<InfoTooltip text="matters.client-invoicing-deadline-tooltip" />}
                            name="invoicingDeadlineDay"
                            options={Array.from({ length: 31 }, (_, i) => ({
                                id: i + 1,
                                name: (i + 1).toString(),
                            }))}
                            setValue={setValue}
                            getOptionLabel="name"
                            getOptionIdentifier="id"
                        />
                    ) : null}
                    {rules ? (
                        <TextAreaInput
                            control={control}
                            label="matters.client-external-billing-info"
                            placeholder="matters.client-external-billing-info"
                            tooltip={<InfoTooltip text="matters.client-external-billing-info-tooltip" />}
                            name="externalBillingInfo"
                        />
                    ) : null}
                    {delivery ? (
                        <TextAreaInput
                            control={control}
                            label="contacts.billing.invoice-archive-email"
                            placeholder="contacts.billing.invoice-archive-email"
                            name="invoiceArchiveEmail"
                            validate={validateEmails}
                            onBlur={removeEmptyStringsFromEmailList}
                        />
                    ) : null}
                    {recipients ? (
                        <DynamicMultiSelectInput
                            setValue={setValue}
                            control={control}
                            label="contacts.billing.invoice-payment-notification-to"
                            name="paymentNotificationReceivers"
                            paginated={{
                                fetch: useLazyGetEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                            }}
                            getOptionLabel="name"
                            getOptionIdentifier="id"
                            placeholder="contacts.billing.invoice-payment-notification-to"
                        />
                    ) : null}
                </>
            ) : null}
        </>
    );
};
